"use client";

import type { SupTransactionType } from "@/types";
import React, { type PropsWithChildren, createContext, useCallback, useContext, useState, useEffect } from "react";

import { SupCopy } from "@repo/core-ui";
import { SupText } from "@repo/core-ui";
import dictionaryConfig from "@/../config/dictionary.config";
import { notifyMessage } from "@/utils";

interface Web3DebugContextProps {
  addCalculate?: (query: Object | undefined) => void;
  addStart?: (query: Object | undefined) => void;
  addRouterSimulation?: (query: Object | undefined) => void;
  addError?: (error: Error | string | undefined) => void;
  copyDebugSummary?: () => void;
  reset?: () => void;
}

interface Web3DebugStateProps extends PropsWithChildren {
  type: SupTransactionType;
}

const Web3DebugContext = createContext<Web3DebugContextProps>({});

export function useWeb3DebugContext() {
  return useContext(Web3DebugContext);
}

export default function Web3DebugState({ children, type }: Web3DebugStateProps) {
  useEffect(() => {
    // eslint-disable-next-line no-shadow-restricted-names
    (function (global, undefined) {
      // @ts-ignore
      if (global.BigInt.prototype.toJSON === undefined) {
        // @ts-ignore
        global.Object.defineProperty(global.BigInt.prototype, "toJSON", {
          value: function () {
            return this.toString();
          },
          configurable: true,
          enumerable: false,
          writable: true,
        });
      }
      // @ts-ignore
    })(window !== void 0 ? window : typeof global !== void 0 ? global : typeof self !== void 0 ? self : this);
  }, []);

  const [calculateRequest, setCalculateRequest] = useState<string | undefined>();
  const [startRequest, setStartRequest] = useState<string | undefined>();
  const [routerSimulation, setRouterSimulation] = useState<string | undefined>();
  const [error, setError] = useState<Error | string | undefined>();

  const addCalculate = useCallback((query: Object | undefined = undefined) => {
    setCalculateRequest(query ? JSON.stringify(query) : undefined);
  }, []);

  const addStart = useCallback((query: Object | undefined = undefined) => {
    setStartRequest(query ? JSON.stringify(query) : undefined);
  }, []);

  const addRouterSimulation = useCallback((query: Object | undefined = undefined) => {
    setRouterSimulation(query ? JSON.stringify(query) : undefined);
  }, []);

  const addError = useCallback((error: Error | string | undefined = undefined) => {
    setError(error);
  }, []);

  const copyDebugSummary = useCallback(async () => {
    const debugData = {
      calculateRequest,
      startRequest,
      routerSimulation,
      error,
      type,
    };

    const text = JSON.stringify(debugData, null, 2);

    await navigator.clipboard.writeText(text);
    notifyMessage(
      "Copied to clipboard successfully",
      `Serialized TX debug data copied`,
      "success",
      <SupCopy />,
      undefined,
      10000,
      <SupText
        as="button"
        className="h-10 border border-current px-2"
        type="button"
        variant="button"
        onClick={() => {
          // @ts-ignore
          if (window && window.MavaMavaWebChatToggle !== undefined) {
            // @ts-ignore
            window.MavaMavaWebChatToggle();
          } else {
            notifyMessage("Mava not available", "Please contact us directly using Discord", "error");
          }
        }}
      >
        {dictionaryConfig.intercomButtonCaption}
      </SupText>
    );
  }, [calculateRequest, error, routerSimulation, startRequest, type]);

  const reset = useCallback(() => {
    setCalculateRequest(undefined);
    setError(undefined);
    setStartRequest(undefined);
    setRouterSimulation(undefined);
  }, []);

  const context = {
    addCalculate,
    addError,
    addStart,
    addRouterSimulation,
    copyDebugSummary,
    reset,
  };

  return <Web3DebugContext.Provider value={context}>{children}</Web3DebugContext.Provider>;
}
